// @ts-nocheck
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import constants from "@store/constants";
import Clickable, { ClickablePresentationProps } from "@store/MobiusOverrides/Clickable/Clickable";
import CollapsableInviteYourColleague from "@store/Library/Components/CollapsableInviteYourColleague";
import CollapsableSideMenu from "@store/Library/Components/CollapsableSideMenu";
import ExpandMenuLine from "@store/Library/Components/Icons/ExpandMenu/ExpandMenuLine";
import CollapseMenuLine from "@store/Library/Components/Icons/CollapseMenu/CollapseMenuLine";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useMemo, useState } from "react";
import { css } from "styled-components";
import { focusStyles } from "@toolkit/Themes/common/mixins/common";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";

const enum fields {
    showMenu = "showMenu",
    menuRootPage = "menuRootPage",
    menuShowRootPage = "menuShowRootPage",
    menuShowSignOut = "menuShowSignOut",
    showInviteYourColleague = "showInviteYourColleague",
}

export interface CollapsableSideColumnLayoutProps extends WidgetProps {
    fields: {
        [fields.showMenu]: boolean;
        [fields.menuRootPage]: LinkFieldValue;
        [fields.menuShowRootPage]: boolean;
        [fields.menuShowSignOut]: boolean;
        [fields.showInviteYourColleague]: boolean;
    };
}

export interface CollapsableSideColumnLayoutStyles {
    wrapper?: InjectableCss;
    sideColumnWrapper?: InjectableCss;
    sideColumnHeaderWrapper?: InjectableCss;
    sideColumnContentWrapper?: InjectableCss;
    mainColumnWrapper?: InjectableCss;
    toggleLink?: ClickablePresentationProps;
    toggleIcon?: IconPresentationProps;
    toggleTooltip?: TooltipPresentationProps;
}

export const collapsableSideColumnLayoutStyles: CollapsableSideColumnLayoutStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(24, 40)};

            ${breakpoint("md")} {
                flex-direction: row;
            }
        `,
    },
    sideColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-shrink: 0;
            min-width: 0; /* Prevents overflow of content */
            border-right: 1px solid ${getColor("common.borderLight")};

            ${breakpoint("sm", "down")} {
                display: none;
            }
        `,
    },
    sideColumnHeaderWrapper: {
        css: css`
            display: flex;
            padding-right: ${pxToRem(12)};
            padding-bottom: ${pxToRem(8)};
            border-bottom: 1px solid ${getColor("common.borderLight")};
            color: ${getColor("text.light")};
        `,
    },
    toggleLink: {
        css: css`
            ${focusStyles()}
            transition: ${transitionHoverStyles("color")};

            &:hover,
            &:active {
                color: ${getColor("primary")};
            }
        `,
    },
    toggleIcon: {
        size: 22,
        color: "inherit",
    },
    toggleTooltip: {
        direction: "right",
        mergeCss: true,
        cssOverrides: {
            tooltipWrapper: css`
                margin: 0;
            `,
            tooltipBody: css`
                padding: ${pxToRem(8, 16)};
            `,
        },
    },
    sideColumnContentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            justify-content: space-between;
            gap: ${pxToRem(32)};
            padding: ${pxToRem(4, 12, 4, 0)};
        `,
    },
    mainColumnWrapper: {
        css: css`
            flex-grow: 1;
            min-width: 0; /* Prevents overflow of content */
        `,
    },
};

const CollapsableSideColumnLayout: FC<CollapsableSideColumnLayoutProps> = ({
    id,
    fields: { showMenu, menuRootPage, menuShowRootPage, menuShowSignOut, showInviteYourColleague },
}) => {
    const [isCollapsed, setIsCollapsed] = useState(
        () => getCookie(constants.cookies.sideColumnLayoutCollapsed) === "true",
    );

    const styles = useMemo(() => {
        return mergeProps<CollapsableSideColumnLayoutStyles>(collapsableSideColumnLayoutStyles, {
            sideColumnWrapper: {
                css: css`
                    ${breakpoint("md")} {
                        width: ${pxToRem(isCollapsed ? 69 : 241)};
                    }
                `,
            },
            sideColumnHeaderWrapper: {
                css: css`
                    justify-content: ${isCollapsed ? "center" : "flex-end"};
                `,
            },
        });
    }, [isCollapsed]);

    const toggleText = isCollapsed ? translate("Expand menu") : translate("Collapse menu");

    return (
        <StyledWrapper {...styles.wrapper}>
            {(showMenu || showInviteYourColleague) && (
                <StyledWrapper {...styles.sideColumnWrapper}>
                    <StyledWrapper {...styles.sideColumnHeaderWrapper}>
                        <Tooltip
                            {...styles.toggleTooltip}
                            displayOnHover
                            text={toggleText}
                            triggerIsClickable
                            triggerComponent={
                                <Clickable
                                    {...styles.toggleLink}
                                    onClick={() => {
                                        setIsCollapsed(x => {
                                            setCookie(constants.cookies.sideColumnLayoutCollapsed, String(!x));
                                            return !x;
                                        });
                                    }}
                                >
                                    <Icon
                                        {...styles.toggleIcon}
                                        src={isCollapsed ? ExpandMenuLine : CollapseMenuLine}
                                    />
                                    <VisuallyHidden>{toggleText}</VisuallyHidden>
                                </Clickable>
                            }
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.sideColumnContentWrapper}>
                        {showMenu && (
                            <CollapsableSideMenu
                                isCollapsed={isCollapsed}
                                nodeId={menuRootPage?.value}
                                showRootPage={menuShowRootPage}
                                showSignOut={menuShowSignOut}
                            />
                        )}
                        {showInviteYourColleague && <CollapsableInviteYourColleague isCollapsed={isCollapsed} />}
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.mainColumnWrapper}>
                <Zone contentId={id} zoneName="Content" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CollapsableSideColumnLayout,
    definition: {
        group: constants.widgetGroup.layout,
        displayName: "Collapsable Side Column Layout",
        icon: "table-layout",
        fieldDefinitions: [
            {
                name: fields.showMenu,
                displayName: "Show Menu",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
                variant: "toggle",
            },
            {
                name: fields.menuRootPage,
                displayName: "Root Page",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                allowUrls: x => false,
                allowCategories: x => false,
                isVisible: x => x.fields.showMenu,
            },
            {
                name: fields.menuShowRootPage,
                displayName: "Show Root Page",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: x => x.fields.showMenu,
            },
            {
                name: fields.menuShowSignOut,
                displayName: 'Show "Sign Out"',
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: x => x.fields.showMenu,
            },
            {
                name: "menuSeparator",
                editorTemplate: "HorizontalRule",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.showInviteYourColleague,
                displayName: "Show Invite Your Colleague",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
                variant: "toggle",
            },
        ],
    },
};

export default widgetModule;
